import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_AD_HISTORY_STATUS, GET_AD_HISTORY_WINNERS, REFRESH_AD_HISTORY } from '@/graphql/ad-history-queries'
import { SET_GENERATION_BASIS_DECISION, SET_GENERATION_BASIS_MODE } from '@/graphql/generation-basis-queries'
import {
    BASIS_ACTION_FAILED_MESSAGE,
    BASIS_ITEM_UNKNOWN,
    BASIS_ITEM_UNKNOWN_MESSAGE,
    errorCodeOf,
} from '@/components/generationBasis'
import type { GenerationBasisDecisionValue, GenerationBasisMode } from '@/components/generationBasis'
import {
    AD_HISTORY_UNAVAILABLE,
    AD_HISTORY_UNAVAILABLE_MESSAGE,
    REFRESH_LIMIT_HOURS,
    adHistoryErrorKind,
    formatMoment,
} from './adHistoryUtils'
import type { AdHistoryErrorKind } from './adHistoryUtils'
import type { AdHistoryRefreshResult, AdHistoryStatus, AdHistoryWinners, GenerationBasisPurpose } from './types'

/** The ad history source of the shared generation basis. */
export const AD_HISTORY_SOURCE = 'ad_history'

interface UseAdHistoryOptions {
    campaignId: string | null | undefined
    purpose: GenerationBasisPurpose
}

export interface AdHistoryController {
    campaignId: string | null
    purpose: GenerationBasisPurpose
    /** The status is being read for the first time */
    loading: boolean
    /** The winners are being read (also after the purpose changed) */
    winnersLoading: boolean
    /** Set when the status or the winners could not be read; `retry` reads both again */
    error: AdHistoryErrorKind | null
    status: AdHistoryStatus | null
    winners: AdHistoryWinners | null
    /** A change (approve, exclude, mode) is being saved */
    changing: boolean
    /** A request to refresh is being sent */
    refreshing: boolean
    /** Notice about the last change or refresh request, in plain words */
    message: string | null
    decide: (itemKey: string, decision: GenerationBasisDecisionValue | null) => Promise<void>
    setMode: (mode: Exclude<GenerationBasisMode, 'off'>) => Promise<void>
    refresh: () => Promise<void>
    retry: () => Promise<void>
    /** Forgets the notice about the last change or refresh request */
    clearMessage: () => void
}

const refreshMessage = (result: AdHistoryRefreshResult): string => {
    if (result.requested) return 'Refresh requested. The results are read at the next hourly pass.'
    switch (result.reason) {
        case 'too_soon': {
            const next = formatMoment(result.nextAllowedAt)
            return `Refreshing is limited to once every ${REFRESH_LIMIT_HOURS} hours${next ? `; you can refresh again after ${next}` : ''}.`
        }
        case 'no_connection':
            return 'Connect a Meta ad account to read your results.'
        case 'not_enabled':
            return 'Learning from your own ad history is not switched on for this campaign.'
        default:
            return 'The refresh could not be requested.'
    }
}

/**
 * The state of the daily read of a licence's ad results, its winning copy for a purpose, and the owner's choices
 * (approve, exclude, restore, manual or automated, refresh). Decisions and the mode are the shared generation basis
 * ones for the ad history source; the winners are read again after every change.
 */
export const useAdHistory = ({ campaignId, purpose }: UseAdHistoryOptions): AdHistoryController => {
    const skip = !campaignId
    const [pending, setPending] = useState(0)
    const [message, setMessage] = useState<string | null>(null)
    const statusQuery = useQuery<{ adHistoryStatus: AdHistoryStatus }>(GET_AD_HISTORY_STATUS, {
        variables: { campaignId },
        skip,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })
    const winnersQuery = useQuery<{ adHistoryWinners: AdHistoryWinners }>(GET_AD_HISTORY_WINNERS, {
        variables: { campaignId, purpose },
        skip,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })
    const [saveDecision] = useMutation(SET_GENERATION_BASIS_DECISION)
    const [saveMode] = useMutation(SET_GENERATION_BASIS_MODE)
    const [requestRefresh, { loading: refreshing }] = useMutation<{ refreshAdHistory: AdHistoryRefreshResult }>(
        REFRESH_AD_HISTORY,
    )

    const failure = statusQuery.error ?? winnersQuery.error
    const error = failure ? adHistoryErrorKind(failure) : null
    // A read that failed leaves stale data behind in the cache result: show none of it
    const status = failure ? null : (statusQuery.data?.adHistoryStatus ?? null)
    const winners = failure ? null : (winnersQuery.data?.adHistoryWinners ?? null)

    const readAgain = async (queries: ('status' | 'winners')[]) => {
        await Promise.all([
            queries.includes('status') ? statusQuery.refetch().catch(() => undefined) : undefined,
            queries.includes('winners') ? winnersQuery.refetch().catch(() => undefined) : undefined,
        ])
    }

    /** Runs a change, then reads the winners again; the choices stay disabled meanwhile. */
    const change = async (action: () => Promise<unknown>) => {
        setPending((count) => count + 1)
        setMessage(null)
        try {
            await action()
        } catch (caught) {
            const code = errorCodeOf(caught)
            setMessage(
                code === BASIS_ITEM_UNKNOWN
                    ? BASIS_ITEM_UNKNOWN_MESSAGE
                    : code === AD_HISTORY_UNAVAILABLE
                      ? AD_HISTORY_UNAVAILABLE_MESSAGE
                      : BASIS_ACTION_FAILED_MESSAGE,
            )
        }
        await readAgain(['winners'])
        setPending((count) => count - 1)
    }

    const decide = (itemKey: string, decision: GenerationBasisDecisionValue | null) =>
        change(() => saveDecision({ variables: { campaignId, source: AD_HISTORY_SOURCE, itemKey, decision } }))

    const setMode = (mode: Exclude<GenerationBasisMode, 'off'>) =>
        change(() => saveMode({ variables: { campaignId, source: AD_HISTORY_SOURCE, mode } }))

    const refresh = async () => {
        setMessage(null)
        try {
            const result = await requestRefresh({ variables: { campaignId } })
            const answer = result.data?.refreshAdHistory
            setMessage(answer ? refreshMessage(answer) : 'The refresh could not be requested.')
        } catch (caught) {
            setMessage(
                errorCodeOf(caught) === AD_HISTORY_UNAVAILABLE
                    ? AD_HISTORY_UNAVAILABLE_MESSAGE
                    : 'The refresh could not be requested. Please try again.',
            )
        }
        await readAgain(['status'])
    }

    return {
        campaignId: campaignId ?? null,
        purpose,
        loading: !skip && !failure && !status,
        winnersLoading: !skip && !failure && !winners,
        error,
        status,
        winners,
        changing: pending > 0,
        refreshing,
        message,
        decide,
        setMode,
        refresh,
        retry: () => readAgain(['status', 'winners']),
        clearMessage: () => setMessage(null),
    }
}
